import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef } from "react";
import { useField, useForm } from "react-final-form";

import {
  ActionButton,
  dataAttribute,
  DataAttributes,
  Heading,
  NoDataTile,
  Pivot,
  PivotItem,
  Stack
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { appointmentFormNameOf } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/AppointmentForm.types.ts";
import { AppointmentFormContext } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/context/AppointmentFormContext.ts";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { SecondColumnContent } from "@shared-types/booking/second-column-content.enum.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { SecondColumnWrapper } from "../../SecondColumnWrapper.tsx";
import { ActiveConditionList } from "../active-condition-list/ActiveConditionList.tsx";
import { ConditionContext } from "../context/ConditionContext.ts";
import { ConditionHelper } from "../context/ConditionHelper.ts";
import {
  ConditionDetailLabels,
  ConditionModalFormValues,
  ConditionPivotHeader
} from "./Condition.types.ts";
import { ConditionModal } from "./ConditionModal.tsx";

export const ConditionDetails: React.FC = observer(() => {
  const {
    episodeOfCare,
    setInitialEpisodesOfCare,
    setEpisodeOfCareId,
    setCreatedEpisodesOfCare,
    conditionAppointmentFormHelper,
    selectedProviders
  } = useContext(AppointmentFormContext);

  const root = useStores();

  const patientEpisodesOfCare =
    conditionAppointmentFormHelper.getSortedEpisodesOfCare();

  const conditionsDischarged = patientEpisodesOfCare.filter(
    x => x.discharged ?? false
  );

  const conditionsInProgress = root.core.hasPermissions(
    Permission.OnHoldClaimAllowed
  )
    ? patientEpisodesOfCare.filter(x => !(x.discharged || x.claim?.isOnHold))
    : patientEpisodesOfCare.filter(x => !x.discharged);

  const conditionsOnHold = patientEpisodesOfCare.filter(
    x => x.claim?.isOnHold ?? false
  );

  const showOnHoldCondition = root.core.hasPermissions([
    Permission.OnHoldClaimAllowed,
    Permission.ClaimRead
  ]);

  setInitialEpisodesOfCare(patientEpisodesOfCare);

  const form = useForm<AppointmentFormValues>();
  const {
    input: { value: patientId }
  } = useField(appointmentFormNameOf("patientId"), {
    subscription: { value: true }
  });

  const {
    input: { value: sendDemographicForm }
  } = useField(appointmentFormNameOf("sendDemographicForm"), {
    subscription: { value: true }
  });

  const updateForm = (episodeOfCare: EpisodeOfCare) => {
    form.batch(() => {
      form.change(appointmentFormNameOf("episodeOfCareId"), episodeOfCare.id);
      form.change(
        appointmentFormNameOf("sendAccForm"),
        episodeOfCare.sendAccForm
      );
      if (
        sendDemographicForm &&
        sendDemographicForm !== episodeOfCare.sendAccForm
      )
        form.change(
          appointmentFormNameOf("sendDemographicForm"),
          episodeOfCare.sendAccForm
        );
    });
  };

  const conditionHelper = useRef<ConditionHelper>(
    new ConditionHelper(root, patientId, {
      onSubmit: async (
        values: ConditionModalFormValues,
        helper: ConditionHelper
      ) => {
        await helper.submitAddConditionValues(values, selectedProviders[0]);
      },
      onSubmitSucceeded: async episodeOfCare => {
        const episodesOfCare = conditionAppointmentFormHelper.episodesOfCare;
        runInAction(() => {
          conditionAppointmentFormHelper.conditionsPromise.set(
            Promise.resolve([episodeOfCare, ...episodesOfCare])
          );
        });

        setEpisodeOfCareId(episodeOfCare.id);
        //storing new created Conditions
        setCreatedEpisodesOfCare(episodeOfCare);
        //This is updating the AppointmentFormFields with values form the ClaimManagementModalForm once submitted
        updateForm(episodeOfCare);
      }
    })
  );

  useEffect(() => {
    if (
      !patientId &&
      root.booking.ui.currentAppointment?.secondColumnContent ===
        SecondColumnContent.conditionDetails
    ) {
      root.booking.ui.setSecondColumnContent(
        root.booking.ui.currentAppointment?.secondColumnContent !==
          SecondColumnContent.conditionDetails
          ? SecondColumnContent.conditionDetails
          : undefined
      );
    }
  }, [patientId, root.booking.ui]);

  const header = (
    <Stack
      horizontal
      horizontalAlign="space-between"
      verticalAlign="center"
      grow={1}
    >
      <Heading
        variant="section-heading-light"
        {...dataAttribute(DataAttributes.Element, "second-column-heading")}
      >
        {ConditionDetailLabels.CurrentConditions}
      </Heading>
      <ActionButton
        iconProps={{ iconName: "add" }}
        onClick={() => conditionHelper.current.showConditionModal(false)}
        text={ConditionDetailLabels.New}
        disabled={selectedProviders.length < 1}
      />
    </Stack>
  );

  const defaultSelectedKey = (() => {
    if (episodeOfCare?.discharged) return ConditionPivotHeader.Discharged;
    if (episodeOfCare?.claim?.isOnHold) return ConditionPivotHeader.OnHold;
    return undefined;
  })();

  return (
    <ConditionContext.Provider value={conditionHelper.current}>
      <SecondColumnWrapper heading={header}>
        {patientEpisodesOfCare.length > 0 && patientId ? (
          <Stack styles={{ root: { width: "calc(100% - 2px)" } }}>
            <Pivot defaultSelectedKey={defaultSelectedKey}>
              <PivotItem
                itemCount={conditionsInProgress.length}
                headerText={ConditionPivotHeader.InProgress}
                itemKey={ConditionPivotHeader.InProgress}
              >
                <ActiveConditionList episodesOfCare={conditionsInProgress} />
              </PivotItem>
              <PivotItem
                itemCount={conditionsDischarged.length}
                headerText={ConditionPivotHeader.Discharged}
                itemKey={ConditionPivotHeader.Discharged}
              >
                <ActiveConditionList episodesOfCare={conditionsDischarged} />
              </PivotItem>
              {showOnHoldCondition && (
                <PivotItem
                  itemCount={conditionsOnHold.length}
                  headerText={ConditionPivotHeader.OnHold}
                  itemKey={ConditionPivotHeader.OnHold}
                >
                  <ActiveConditionList episodesOfCare={conditionsOnHold} />
                </PivotItem>
              )}
            </Pivot>
          </Stack>
        ) : (
          <NoDataTile
            styles={{ root: { width: "100%", justifyContent: "center" } }}
            textProps={{
              text: ConditionDetailLabels.NoConditionsToDisplay
            }}
            greyView
            showBoxShadow={false}
            linkProps={{
              text: ConditionDetailLabels.CreateNewCondition,
              onClick: () => {
                conditionHelper.current.showConditionModal(false);
              }
            }}
          />
        )}
      </SecondColumnWrapper>
      <ConditionModal hideAddDiagnosisOption={true} />
    </ConditionContext.Provider>
  );
});
